<template>
  <div class="audio-player">
    <div class="controls">
      <button class="play-button" @click="togglePlay">
        {{ isPlaying ? '暂停' : '播放' }}
      </button>
      <div class="progress-container">
        <div class="progress-bar" @click="seekAudio">
          <div class="progress" :style="{ width: progress + '%' }"></div>
        </div>
        <div class="time-display">
          <span>{{ formatTime(currentTime) }}</span>
          <span>{{ formatTime(duration) }}</span>
        </div>
      </div>
      <div class="volume-control">
        <input 
          type="range" 
          min="0" 
          max="1" 
          step="0.1" 
          v-model="volume" 
          @change="updateVolume"
        />
      </div>
    </div>
    <audio 
      ref="audioElement"
      :src="audioSrc"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      @ended="onEnded"
    ></audio>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'AudioPlayer',
  props: {
    audioSrc: {
      type: String,
      required: true
    },
    lessonTitle: {
      type: String,
      default: ''
    },
    courseIndex: {
      type: Number,
      required: true
    },
    lessonIndex: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    const store = useStore()
    const currentTime = ref(0)
    const duration = ref(0)
    const volume = ref(1)
    const audioElement = ref(null)
    
    const getCurrentAudio = computed(() => store.getters.getCurrentAudio)
    
    const isPlaying = computed({
      get: () => {
        return getCurrentAudio.value.isPlaying && 
               getCurrentAudio.value.courseIndex === props.courseIndex && 
               getCurrentAudio.value.lessonIndex === props.lessonIndex
      },
      set: (value) => {
        store.dispatch('setAudioPlaying', value)
      }
    })
    
    const progress = computed({
      get: () => {
        return getCurrentAudio.value.courseIndex === props.courseIndex && 
               getCurrentAudio.value.lessonIndex === props.lessonIndex ? 
               getCurrentAudio.value.progress : 0
      },
      set: (value) => {
        store.dispatch('updateAudioProgress', value)
      }
    })
    
    const togglePlay = () => {
      if (isPlaying.value) {
        audioElement.value.pause()
        isPlaying.value = false
      } else {
        audioElement.value.play()
        isPlaying.value = true
        // 告诉Vuex当前播放的是哪个课程
        store.dispatch('updateCurrentAudio', {
          courseIndex: props.courseIndex,
          lessonIndex: props.lessonIndex
        })
      }
    }
    
    const onTimeUpdate = () => {
      currentTime.value = audioElement.value.currentTime
      progress.value = (audioElement.value.currentTime / audioElement.value.duration) * 100
    }
    
    const onLoadedMetadata = () => {
      duration.value = audioElement.value.duration
    }
    
    const onEnded = () => {
      isPlaying.value = false
      currentTime.value = 0
      progress.value = 0
    }
    
    const updateVolume = () => {
      audioElement.value.volume = volume.value
    }
    
    const formatTime = (seconds) => {
      if (isNaN(seconds) || seconds === Infinity) return '00:00'
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    
    const seekAudio = (event) => {
      const progressBar = event.currentTarget
      const clickPosition = event.offsetX
      const progressBarWidth = progressBar.clientWidth
      const seekPercentage = (clickPosition / progressBarWidth) * 100
      
      // 设置音频播放位置
      audioElement.value.currentTime = (seekPercentage / 100) * audioElement.value.duration
      
      // 更新进度条
      progress.value = seekPercentage
    }
    
    // 监听其他课程播放状态变化
    watch(getCurrentAudio, (newValue) => {
      // 如果开始播放的是其他课程，暂停当前课程
      if (newValue.isPlaying && 
          (newValue.courseIndex !== props.courseIndex || 
           newValue.lessonIndex !== props.lessonIndex)) {
        audioElement.value?.pause()
      }
    }, { deep: true })
    
    return {
      currentTime,
      duration,
      volume,
      audioElement,
      isPlaying,
      progress,
      togglePlay,
      onTimeUpdate,
      onLoadedMetadata,
      onEnded,
      updateVolume,
      formatTime,
      seekAudio
    }
  }
}
</script>

<style scoped>
.audio-player {
  width: 100%;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 12px;
  margin-top: 15px;
}

.controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.play-button {
  background-color: #2c3e50;
  color: white;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
}

.progress-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.progress-bar {
  height: 6px;
  background-color: #ddd;
  border-radius: 3px;
  position: relative;
  cursor: pointer;
}

.progress {
  height: 100%;
  background-color: #2c3e50;
  border-radius: 3px;
  position: absolute;
  top: 0;
  left: 0;
}

.time-display {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
}

.volume-control {
  width: 80px;
}

.volume-control input {
  width: 100%;
}
</style> 